// WOS-BUNDLE: the pure rules of company tags, shared by the screens and the in-memory twin.
// Work OS `ensureTag` (domain/task-extras.ts): a name is trimmed and may not be empty; a tag made
// without a colour takes `TAG_COLORS[|hash(name)| % 8]` with `hash = h * 31 + charCode` (32-bit);
// names are unique ignoring case. `zinc` is the neutral colour a person may pick.
import type { OperationOutput } from "@companyos/sdk";

export type TagView = OperationOutput<"tags.list">["data"][number];
export type TagColor = TagView["color"];

/** Work OS `TAG_COLORS`, in its order (the colour picked from a name indexes this list). */
export const TAG_COLORS = [
  "violet",
  "sky",
  "emerald",
  "amber",
  "rose",
  "indigo",
  "teal",
  "orange",
] as const satisfies readonly TagColor[];

/** Every colour a tag may take, in swatch order (neutral first). */
export const TAG_SWATCHES: readonly { color: TagColor; label: string }[] = [
  { color: "zinc", label: "Grey" },
  { color: "violet", label: "Violet" },
  { color: "sky", label: "Sky" },
  { color: "emerald", label: "Emerald" },
  { color: "amber", label: "Amber" },
  { color: "rose", label: "Rose" },
  { color: "indigo", label: "Indigo" },
  { color: "teal", label: "Teal" },
  { color: "orange", label: "Orange" },
];

/** Work OS `colorFor(name)`: the 32-bit `h * 31 + charCode` hash, made positive, mod 8. */
export function tagColorFor(name: string): TagColor {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return TAG_COLORS[Math.abs(h) % TAG_COLORS.length] ?? "violet";
}

/** A tag name as stored (trimmed), or null when nothing is left. */
export function cleanTagName(name: string): string | null {
  const t = name.trim().replace(/\s+/g, " ");
  return t ? t.slice(0, 60) : null;
}

/** The tag of that name, ignoring case (Work OS get-or-create matches this way). */
export function tagNamed<T extends { name: string }>(tags: readonly T[], name: string): T | null {
  const want = name.trim().toLowerCase();
  return tags.find((t) => t.name.toLowerCase() === want) ?? null;
}

/** The tags to offer in a picker: matching the query, not on the task, by name. */
export function tagOptions<T extends { id: string; name: string }>(
  tags: readonly T[],
  query: string,
  taken: readonly string[],
): T[] {
  const q = query.trim().toLowerCase();
  return tags
    .filter((t) => !taken.includes(t.id) && (!q || t.name.toLowerCase().includes(q)))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Whether the picker offers `Create "name"`: a non-empty name no tag has (ignoring case). */
export function canCreateTag(tags: readonly { name: string }[], query: string): boolean {
  const name = cleanTagName(query);
  return name !== null && tagNamed(tags, name) === null;
}
